import { getWorkspace } from "@/api/workspace";
import type { Workspace } from "@/api/workspace";
import type { NavItem } from "@/mk/types";

export interface AppSection {
    id: string;
    label: string;
    icon: string;
    model?: string;
    group?: string;
    route: string;
    /** shown only to an owner organisation, as the old section was */
    ownerOnly?: boolean;
    modes?: Workspace[];
    designLabel?: string;
}

export interface AppEntry {
    tag: string;
    /** an application that is one list of one model is hidden when that model is */
    model?: string;
    /** what the breadcrumb calls the application, when the sidebar is shorter */
    title?: string;
    id: string;
    label: string;
    icon: string;
    sequence: number;
    route: string;
    group?: string;
    modes?: Workspace[];
    tile?: boolean;
    sections: AppSection[];
}

export interface NavAccess {
    models: Record<string, { read: boolean; write: boolean; create: boolean; unlink: boolean }>;
    boards: Record<string, boolean>;
    apps: Record<string, boolean>;
    icons: Record<string, string>;
    groups: Record<string, boolean>;
}

const ORG_ADMIN = "promaster_base.group_org_admin";

const EXECUTION: Workspace[] = ["execution"];

const DESIGN: Workspace[] = ["design"];
const EMAIL_MANAGER = "promaster_email.group_email_manager";

const REQUEST_SECTIONS: AppSection[] = [
    { id: "general_approval", label: "nav.req.generalApproval", icon: "fa-file-text-o",
      model: "general.approval.request", route: "/requests/general-approvals" },
    { id: "drawing_approval", label: "nav.req.drawingApproval", icon: "fa-object-group",
      model: "drawing.approval.request", route: "/requests/drawing-approvals" },
    { id: "supplier_approval", modes: EXECUTION, label: "nav.req.supplierApproval", icon: "fa-truck",
      model: "request.supplier.approval", route: "/requests/supplier-approvals" },
    { id: "staff_approval", modes: EXECUTION, label: "nav.req.staffApproval", icon: "fa-id-badge",
      model: "request.staff.approval", route: "/requests/staff-approvals" },
    { id: "inspection_concrete", modes: EXECUTION, label: "nav.req.concreteInspection", icon: "fa-cube",
      model: "inspection.concrete.request", route: "/requests/concrete-inspections" },
    { id: "material_approval", modes: EXECUTION, label: "nav.req.materialApproval", icon: "fa-cubes",
      model: "request.material.approval", route: "/requests/material-approvals" },
    { id: "sample_approval", modes: EXECUTION, label: "nav.req.sampleApproval", icon: "fa-clone",
      model: "request.sample.approval", route: "/requests/sample-approvals" },
    { id: "start_new_item", modes: EXECUTION, label: "nav.req.startNewItem", icon: "fa-plus-square",
      model: "start.new.item", route: "/requests/start-new-items" },
    { id: "change_proposal", modes: EXECUTION, label: "nav.req.changeProposal", icon: "fa-exchange",
      model: "change.proposal.request", route: "/requests/change-proposals" },
    { id: "change_request", label: "nav.req.changeRequest", icon: "fa-edit",
      model: "change.request", route: "/requests/change-requests" },
    { id: "general_inspection", label: "nav.req.generalInspection", icon: "fa-search",
      designLabel: "nav.req.designInspection",
      model: "general.inspection.request", route: "/requests/inspections" },
    { id: "work_quantity_sheet", modes: EXECUTION, label: "nav.req.workQuantitySheet", icon: "fa-table",
      model: "work.quantity.sheet", route: "/requests/work-quantity-sheets" },
    { id: "boq_variance", modes: EXECUTION, label: "nav.req.boqVariance", icon: "fa-balance-scale",
      model: "boq.variance.request", route: "/requests/boq-variance-requests" },
    { id: "information", modes: EXECUTION, label: "nav.req.information", icon: "fa-info-circle",
      model: "request.information", route: "/requests/rfis" },
    { id: "project_stop", modes: EXECUTION, label: "nav.req.projectStop", icon: "fa-pause-circle",
      model: "project.stop", route: "/requests/project-stops" },
];

export const SETTINGS_SECTIONS: AppSection[] = [
    { id: "project_tags", label: "nav.settings.projectTags", icon: "fa-tags", model: "project.tags" },
    { id: "materials", label: "nav.settings.materials", icon: "fa-cubes", model: "promaster.material" },
    { id: "partners", label: "nav.settings.partners", icon: "fa-industry",
      model: "promaster.material.partner" },
    { id: "workforce", label: "nav.settings.workforce", icon: "fa-users", model: "promaster.worker" },
    { id: "equipment", label: "nav.settings.equipment", icon: "fa-wrench", model: "promaster.equipment" },
    { id: "units", label: "nav.settings.units", icon: "fa-balance-scale", model: "promaster.unit" },
    { id: "request_type", label: "nav.settings.requestType", icon: "fa-tags", model: "request.type" },
    { id: "request_code", label: "nav.settings.requestCode", icon: "fa-hashtag", model: "request.code" },
    { id: "attachments_type", label: "nav.settings.attachmentsType", icon: "fa-paperclip",
      model: "attachments.type" },
    { id: "concrete_checklist", label: "nav.settings.concreteChecklist", icon: "fa-cube",
      model: "concrete.checklist" },
    { id: "quality_control_items", label: "nav.settings.qualityControlItems",
      icon: "fa-check-square-o", model: "quality.control.item" },
    { id: "job_positions", label: "nav.settings.jobPositions", icon: "fa-id-badge",
      model: "project.position" },
    { id: "notification_settings", label: "nav.settings.notificationSettings", icon: "fa-bell",
      model: "project.notification.settings" },
    { id: "workflow", label: "nav.settings.workflow", icon: "fa-sitemap", model: "permission.template",
      ownerOnly: true,
      group: ORG_ADMIN },
    { id: "policies", label: "nav.settings.policies", icon: "fa-clock-o",
      group: "promaster_sla.group_read_sla" },
    { id: "team", label: "nav.settings.team", icon: "fa-users", group: ORG_ADMIN },
    { id: "users", label: "nav.settings.users", icon: "fa-user-circle-o", group: ORG_ADMIN },
    { id: "delegations", label: "nav.settings.delegations", icon: "fa-user-times", group: ORG_ADMIN },
    { id: "outlook", label: "nav.settings.outlook", icon: "fa-plug", model: "promaster.outlook.config",
      group: EMAIL_MANAGER },
    { id: "mail_outgoing", label: "nav.settings.mailOutgoing", icon: "fa-paper-plane",
      model: "ir.mail_server", group: EMAIL_MANAGER },
    { id: "mail_incoming", label: "nav.settings.mailIncoming", icon: "fa-inbox",
      model: "fetchmail.server", group: EMAIL_MANAGER },
].map((section) => ({ ...section, route: `/settings?section=${section.id}` }));

export const APPS: AppEntry[] = [
    {
        tag: "promaster_projects.projects", id: "projects", label: "nav.executionProjects",
        icon: "fa-building", sequence: 10, route: "/projects?type=execution",
        model: "project.project", modes: EXECUTION, tile: true, sections: [],
    },
    {
        tag: "promaster_projects.projects", id: "design-projects", label: "nav.designProjects",
        icon: "fa-pencil-square-o", sequence: 10, route: "/projects?type=design",
        model: "project.project", modes: DESIGN, tile: true, sections: [],
    },
    {
        tag: "promaster_projects.projects", id: "design-dashboard", label: "nav.designDashboard",
        icon: "fa-tachometer", sequence: 11, route: "/design-dashboard", modes: DESIGN, tile: true,
        sections: [],
    },
    {
        tag: "promaster_email.email_app", id: "email", label: "nav.email",
        icon: "fa-envelope-open-o", sequence: 62, route: "/email?view=inbox",
        sections: [
            { id: "inbox", label: "nav.inbox", icon: "fa-inbox",
              model: "promaster.mail.message", route: "/email?view=inbox" },
            { id: "outbox", label: "nav.outbox", icon: "fa-paper-plane",
              model: "promaster.mail.message", route: "/email?view=outbox" },
            { id: "accounts", label: "nav.mailboxConnection", icon: "fa-plug",
              model: "promaster.mail.account", route: "/email?view=accounts" },
        ],
    },
    {
        tag: "promaster_boq.boq_app", id: "boq", label: "nav.boq",
        icon: "fa-table", sequence: 20, route: "/boq?view=contract",
        sections: [
            { id: "contract", label: "nav.contractBoq", designLabel: "nav.designBoq", icon: "fa-table",
              model: "bill.of.quantities", route: "/boq?view=contract" },
            { id: "execution", modes: EXECUTION, label: "nav.boqExecution", icon: "fa-check-square-o",
              model: "bill.of.quantities", route: "/boq?view=execution" },
            { id: "log", modes: EXECUTION, label: "nav.changeLog", icon: "fa-history",
              model: "boq.execution.history", route: "/boq?view=log" },
        ],
    },
    {
        tag: "promaster_timeline.timeline_app", id: "timeline", label: "nav.timeline",
        icon: "fa-clock-o", sequence: 30, route: "/timeline", sections: [],
    },
    {
        tag: "promaster_alerts.alerts_app", id: "alerts", label: "nav.alerts",
        icon: "fa-bell", sequence: 35, route: "/alerts", model: "pm.alert.event", tile: true,
        sections: [],
    },
    {
        tag: "promaster_ai.ai_app", id: "ai", label: "nav.ai",
        icon: "fa-magic", sequence: 145, route: "/ai", sections: [],
    },
    {
        tag: "promaster_base.manual_app", id: "manual", label: "nav.manual",
        icon: "fa-book", sequence: 125, route: "/manual", sections: [],
    },
    {
        tag: "promaster_requests.requests_app", id: "requests", label: "nav.requests",
        icon: "fa-file-text-o", sequence: 40, route: "/requests/general-approvals",
        sections: REQUEST_SECTIONS,
    },
    {
        tag: "promaster_payment.payment_app", id: "payment", label: "nav.paymentCertificates",
        icon: "fa-money", sequence: 50, route: "/payments?view=payments",
        sections: [
            { id: "payments", label: "nav.paymentCertificates", designLabel: "nav.designPayments",
              icon: "fa-money",
              model: "letter.payment", route: "/payments?view=payments" },
            { id: "discounts", label: "nav.ipcDeductions", icon: "fa-percent",
              model: "extract.discount", route: "/payments?view=discounts" },
        ],
    },
    {
        tag: "promaster_letters.letters_app", id: "letters", label: "nav.letters",
        icon: "fa-envelope", sequence: 60, route: "/letters?view=letters",
        sections: [
            { id: "letters", label: "nav.letters", icon: "fa-envelope",
              model: "project.letter", route: "/letters?view=letters" },
            { id: "memos", label: "nav.internalMemos", icon: "fa-file-text-o",
              model: "project.internal.memo", route: "/letters?view=memos" },
            { id: "templates", label: "nav.letterTemplates", icon: "fa-files-o",
              model: "promaster.letter.template", route: "/letters?view=templates" },
        ],
    },
    {
        tag: "promaster_meetings.committees", id: "committees", label: "nav.projectCommittees",
        icon: "fa-sitemap", sequence: 69, route: "/committees?view=councils", tile: true,
        sections: [
            { id: "councils", label: "nav.committees", icon: "fa-sitemap",
              model: "meeting.council", route: "/committees?view=councils" },
        ],
    },
    {
        tag: "promaster_meetings.meetings", id: "meetings", label: "nav.meetings",
        icon: "fa-users", sequence: 70, route: "/meetings?view=meetings",
        sections: [
            { id: "meetings", label: "nav.meetings", icon: "fa-users",
              model: "meeting.meeting", route: "/meetings?view=meetings" },
            { id: "announcements", label: "nav.announcements", icon: "fa-bullhorn",
              model: "meeting.announcement", route: "/meetings?view=announcements" },
        ],
    },
    {
        tag: "promaster_hse.risk_app", id: "risks", modes: EXECUTION, label: "nav.risks",
        icon: "fa-exclamation-triangle", sequence: 80, route: "/hse?view=risks",
        sections: [
            { id: "risks", label: "nav.riskRegister", icon: "fa-exclamation-triangle",
              model: "promaster.risk.register", route: "/hse?view=risks" },
            { id: "risk-categories", label: "nav.riskCategories", icon: "fa-tags",
              model: "promaster.risk.category", route: "/hse?view=risk-categories" },
        ],
    },
    {
        tag: "promaster_hse.hse_app", id: "hse", modes: EXECUTION, label: "nav.hse",
        icon: "fa-shield", sequence: 81, route: "/hse?view=incidents",
        sections: [
            { id: "incidents", label: "nav.incidents", icon: "fa-ambulance",
              model: "promaster.hse.incident", route: "/hse?view=incidents" },
            { id: "observations", label: "nav.safetyObservations", icon: "fa-eye",
              model: "promaster.hse.observation", route: "/hse?view=observations" },
            { id: "inspections", label: "nav.safetyInspections", icon: "fa-check-square-o",
              model: "promaster.hse.inspection", route: "/hse?view=inspections" },
            { id: "permits", label: "nav.workPermits", icon: "fa-id-card-o",
              model: "promaster.hse.permit", route: "/hse?view=permits" },
            { id: "toolboxes", label: "nav.toolboxMeetings", icon: "fa-users",
              model: "promaster.hse.toolbox", route: "/hse?view=toolboxes" },
            { id: "actions", label: "nav.correctiveActions", icon: "fa-wrench",
              model: "promaster.hse.action", route: "/hse?view=actions" },
            { id: "incident-categories", label: "nav.incidentCategories", icon: "fa-tags",
              model: "promaster.hse.incident.category", route: "/hse?view=incident-categories" },
            { id: "checklist-items", label: "nav.checklistItems", icon: "fa-list-ul",
              model: "promaster.hse.checklist.item", route: "/hse?view=checklist-items" },
        ],
    },
    {
        tag: "promaster_reports.reports_app", id: "reports", label: "nav.reports",
        icon: "fa-calendar-check-o", sequence: 90, route: "/executive",
        sections: [
            { id: "simplified", modes: EXECUTION, label: "nav.simplifiedReport", icon: "fa-line-chart",
              model: "monthly.report", route: "/executive" },
            { id: "builder", label: "nav.reportBuilder", icon: "fa-magic",
              model: "promaster.report.definition", route: "/report-builder" },
            { id: "daily", modes: EXECUTION, label: "nav.dailyReports", icon: "fa-calendar-check-o",
              model: "daily.report", route: "/reports?view=daily" },
            { id: "weekly", label: "nav.weeklyReports", icon: "fa-calendar",
              model: "weekly.report", route: "/reports?view=weekly" },
            { id: "monthly", label: "nav.monthlyReports", icon: "fa-calendar-o",
              model: "monthly.report", route: "/reports?view=monthly" },
            { id: "investigation", modes: EXECUTION, label: "nav.investigationReports", icon: "fa-gavel",
              model: "investigation.report", route: "/reports?view=investigation" },
            { id: "non-conformance", modes: EXECUTION, label: "nav.nonConformanceReports",
              icon: "fa-exclamation-triangle", model: "non.conformance.report",
              route: "/reports?view=non-conformance" },
            { id: "quality-control", modes: EXECUTION, label: "nav.qualityControlReports", icon: "fa-check-square-o",
              model: "quality.control.report", route: "/reports?view=quality-control" },
        ],
    },
    {
        tag: "promaster_identity_guest.party_contracts", id: "contracts",
        label: "nav.partyContracts", icon: "fa-id-card", sequence: 98, route: "/contracts",
        model: "promaster.party.contract",
        sections: [],
    },
    {
        tag: "promaster_dashboard.dashboard_app", id: "dashboard", modes: EXECUTION, label: "nav.performanceCenter",
        title: "pmud.title",
        icon: "fa-tachometer", sequence: 100, route: "/dashboard?section=overview",
        sections: [
            { id: "overview", label: "nav.allProjects", icon: "fa-line-chart",
              route: "/dashboard?section=overview" },
            { id: "project", label: "nav.projectDetails", icon: "fa-folder-open",
              route: "/dashboard?section=project" },
            { id: "parties", label: "nav.projectParties", icon: "fa-users",
              route: "/dashboard?section=parties" },
            { id: "staff", label: "nav.staffTasks", icon: "fa-id-badge", route: "/staff" },
            { id: "requests", label: "nav.requestsSection", icon: "fa-check-square-o",
              route: "/dashboard?section=requests" },
        ],
    },
    {
        tag: "promaster_handover.handover_app", id: "handover", modes: EXECUTION, label: "nav.handover",
        icon: "fa-handshake-o", sequence: 110, route: "/handover?view=handovers",
        sections: [
            { id: "handovers", label: "nav.handovers", icon: "fa-handshake-o",
              model: "project.handover", route: "/handover?view=handovers" },
            { id: "certificates", label: "nav.completionCertificates", icon: "fa-certificate",
              model: "project.completion.certificate", route: "/handover?view=certificates" },
        ],
    },
    {
        tag: "promaster_settings.project_settings", id: "settings", label: "nav.projectSettings",
        icon: "fa-sliders", sequence: 120, route: "/settings?section=project_tags",
        sections: SETTINGS_SECTIONS,
    },
    {
        tag: "promaster_archive.archive_app", id: "archive", label: "nav.archive",
        icon: "fa-archive", sequence: 150, route: "/archive?view=projects",
        sections: [
            { id: "projects", label: "nav.projectArchives", icon: "fa-archive",
              route: "/archive?view=projects" },
            { id: "documents", label: "nav.documentBrowser", icon: "fa-files-o",
              route: "/archive?view=documents" },
            { id: "external", label: "nav.externalArchive", icon: "fa-folder-open-o",
              route: "/archive?view=external" },
            { id: "settings", label: "nav.documentTypes", icon: "fa-sliders",
              model: "project.archive.config", route: "/archive?view=settings" },
        ],
    },
];

export const HOME_LINK_GROUPS = ["promaster_reports.group_read_monthly_report"];

function unique(values: (string | undefined)[]): string[] {
    return [...new Set(values.filter((value): value is string => Boolean(value)))];
}

export function navigationQuery(): string {
    const tags = APPS.map((app) => app.tag);
    const groups = unique([
        ...APPS.map((app) => app.group),
        ...APPS.flatMap((app) => app.sections.map((section) => section.group)),
        ...HOME_LINK_GROUPS,
    ]);
    const models = unique(APPS.flatMap((app) => app.sections.map((section) => section.model)));
    const params = new URLSearchParams({
        tags: tags.join(","), groups: groups.join(","), models: models.join(","),
    });
    return `/navigation?${params.toString()}`;
}

export function canGroup(access: NavAccess | undefined, group?: string): boolean {
    if (!group || !access || access.groups[group] === undefined) {
        return true;
    }
    return access.groups[group] === true;
}

export function sectionAllowed(access: NavAccess | undefined, section: AppSection,
                               isOwnerOrg?: boolean): boolean {
    return canSection(access, section) && (!section.ownerOnly || isOwnerOrg === true);
}

function canSection(access: NavAccess | undefined, section: AppSection): boolean {
    if (!canGroup(access, section.group)) {
        return false;
    }
    if (!section.model || !access) {
        return true;
    }
    return access.models[section.model]?.read !== false;
}

function inMode(entry: { modes?: Workspace[] }, mode: Workspace): boolean {
    return !entry.modes || entry.modes.includes(mode);
}

export function appsFor(mode: Workspace = getWorkspace()): AppEntry[] {
    return APPS.filter((app) => inMode(app, mode));
}

export function sectionsFor(app: AppEntry, mode: Workspace = getWorkspace()): AppSection[] {
    return app.sections.filter((section) => inMode(section, mode));
}

export function sectionLabel(section: AppSection, mode: Workspace = getWorkspace()): string {
    return mode === "design" && section.designLabel ? section.designLabel : section.label;
}

function appRoute(app: AppEntry, mode: Workspace): string {
    const sections = sectionsFor(app, mode);
    if (!app.sections.length || sections.some((section) => section.route === app.route)) {
        return app.route;
    }
    return sections[0]?.route ?? app.route;
}

export function navTree(t: (key: string) => string, access: NavAccess | undefined,
                        activeApp?: string, isOwnerOrg?: boolean,
                        mode: Workspace = getWorkspace()): NavItem[] {
    const items: NavItem[] = [];
    for (const app of appsFor(mode).sort((a, b) => a.sequence - b.sequence)) {
        if (access && access.apps[app.tag] === false) {
            continue;
        }
        if (!canGroup(access, app.group)) {
            continue;
        }
        if (app.model && access && access.models[app.model]?.read === false) {
            continue;
        }
        const children = sectionsFor(app, mode)
            .filter((section) => canSection(access, section))
            .filter((section) => !section.ownerOnly || isOwnerOrg === true)
            .map((section) => ({
                id: `${app.id}:${section.id}`,
                label: t(sectionLabel(section, mode)),
                icon: section.icon,
                selectable: true,
            }));
        items.push({
            id: app.id,
            label: t(app.label),
            icon: app.icon,
            imgSrc: app.tile ? undefined : access?.icons[app.tag],
            tile: app.tile || !access?.icons[app.tag],
            selectable: true,
            defaultOpen: app.id === activeApp,
            children: children.length ? children : undefined,
        });
    }
    return items;
}

export function routeFor(id: string, mode: Workspace = getWorkspace()): string | null {
    const [appId, sectionId] = id.split(":");
    const app = APPS.find((entry) => entry.id === appId);
    if (!app) {
        return null;
    }
    if (!sectionId) {
        return appRoute(app, mode);
    }
    return sectionsFor(app, mode).find((section) => section.id === sectionId)?.route
        ?? appRoute(app, mode);
}

/** The section the reader is on, as the sidebar marks it.
 *
 *  A section the reader may not open is not in the sidebar at all, so the
 *  application falls back to the first one it does offer — which is what the
 *  page itself shows.
 */
export function activeFor(pathname: string, search: string,
                          allowed?: (app: AppEntry, section: AppSection) => boolean,
                          mode: Workspace = getWorkspace())
        : string | undefined {
    const full = `${pathname}${search}`;
    const apps = appsFor(mode);
    let best: { id: string; length: number } | null = null;
    for (const app of apps) {
        for (const section of sectionsFor(app, mode)) {
            const exact = full === section.route;
            const bare = section.route.indexOf("?") === -1 && pathname === section.route;
            if (exact || bare) {
                const id = `${app.id}:${section.id}`;
                const weight = section.route.length + (exact ? 1000 : 0);
                if (!best || weight > best.length) {
                    best = { id, length: weight };
                }
            }
        }
    }
    if (best) {
        const [appId, sectionId] = best.id.split(":");
        const app = apps.find((entry) => entry.id === appId);
        const section = app?.sections.find((entry) => entry.id === sectionId);
        if (app && section && allowed && !allowed(app, section)) {
            const first = sectionsFor(app, mode).find((entry) => allowed(app, entry));
            return first ? `${app.id}:${first.id}` : app.id;
        }
        return best.id;
    }
    for (const app of apps) {
        if (pathname === app.route.split("?")[0]) {
            const first = sectionsFor(app, mode)
                .find((section) => section.route.split("?")[0] === pathname);
            return first ? `${app.id}:${first.id}` : app.id;
        }
    }
    return undefined;
}

const APP_MODELS: Record<string, string> = {
    "project.timeline": "/timeline",
};

export function recordRoute(model: string, id: number | false | undefined,
                            mode: Workspace = getWorkspace()): string | null {
    if (!id) {
        return null;
    }
    for (const app of appsFor(mode)) {
        const section = sectionsFor(app, mode).find((entry) => entry.model === model);
        if (section) {
            const joiner = section.route.includes("?") ? "&" : "?";
            return `${section.route}${joiner}open=${id}`;
        }
        if (app.model === model) {
            const joiner = app.route.includes("?") ? "&" : "?";
            return `${app.route}${joiner}open=${id}`;
        }
    }
    const route = APP_MODELS[model];
    return route ? `${route}?open=${id}` : null;
}

export function trailFor(t: (key: string) => string, active: string | undefined) {
    if (!active) {
        return [];
    }
    const [appId, sectionId] = active.split(":");
    const app = APPS.find((entry) => entry.id === appId);
    if (!app) {
        return [];
    }
    const crumbs = [{ id: `crumb:${active}`, label: t(app.title ?? app.label), icon: app.icon }];
    const section = app.sections.find((entry) => entry.id === sectionId);
    if (section) {
        crumbs.push({ id: `crumb:${section.id}`, label: t(sectionLabel(section)), icon: section.icon });
    }
    return crumbs;
}
